import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  SUPPORT_BODY_MAX,
  SUPPORT_REF_TYPES,
  SUPPORT_REF_TYPE_LABELS,
  SUPPORT_SUBJECT_MAX,
  SUPPORT_TICKET_CATEGORIES,
  SUPPORT_TICKET_CATEGORY_LABELS,
  categoryForRef,
  type SupportContextResponse,
  type SupportRefType,
  type SupportTicketCategory,
  type SupportTicketDetailResponse,
} from '@nexus/shared';

import { ApiError, apiGet, apiSend } from '../lib/api';
import { Alert, Button, Card, PageHeader, SelectField, TextField } from '../ui';

/**
 * Opening a ticket.
 *
 * The form is prefilled from the address when a page sent somebody here with
 * a reason — `?category=SUBMISSION_REVIEW&refType=PostSubmission&refId=…&subject=…&body=…`
 * from "Request a re-review", `?category=PAYOUT&refType=Payout&refId=…` from
 * a wallet row — so the creator arrives with the context already chosen and
 * only their own words to add. With no prefill the context picker lists
 * their submissions, payouts, connections and bonuses, because "which one"
 * is the first question the operator would otherwise have to ask back.
 */
const isCategory = (v: string | null): v is SupportTicketCategory =>
  v !== null && (SUPPORT_TICKET_CATEGORIES as readonly string[]).includes(v);
const isRefType = (v: string | null): v is SupportRefType =>
  v !== null && (SUPPORT_REF_TYPES as readonly string[]).includes(v);

export function SupportNew(): React.JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const prefillRefType = params.get('refType');
  const prefillRefId = params.get('refId');
  const prefillCategory = params.get('category');

  const [category, setCategory] = useState<SupportTicketCategory>(
    isCategory(prefillCategory) ? prefillCategory : isRefType(prefillRefType) ? categoryForRef(prefillRefType) : 'OTHER',
  );
  const [subject, setSubject] = useState(params.get('subject') ?? '');
  const [body, setBody] = useState(params.get('body') ?? '');
  /* `<type>:<id>` or '' for "nothing in particular". */
  const [ref, setRef] = useState(
    isRefType(prefillRefType) && prefillRefId ? `${prefillRefType}:${prefillRefId}` : '',
  );
  const [context, setContext] = useState<SupportContextResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadContext = useCallback(async () => {
    try {
      setContext(await apiGet<SupportContextResponse>('/support/context'));
    } catch {
      /* The picker is a convenience; the form works without it. */
      setContext({ submissions: [], payouts: [], accounts: [], bonuses: [] });
    }
  }, []);

  useEffect(() => {
    void loadContext();
  }, [loadContext]);

  /* Picking a context picks the category, unless the person already chose one. */
  function pickRef(value: string): void {
    setRef(value);
    const [type] = value.split(':');
    if (isRefType(type ?? null)) setCategory(categoryForRef(type as SupportRefType));
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const [type, id] = ref.split(':');
      const created = await apiSend<SupportTicketDetailResponse>('POST', '/support/tickets', {
        category,
        subject: subject.trim(),
        body: body.trim(),
        ...(isRefType(type ?? null) && id ? { ref: { type, id } } : {}),
      });
      navigate(`/support/${created.ticket.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open the ticket.');
      setBusy(false);
    }
  }

  const options: Array<{ value: string; label: string; group: string }> = context
    ? [
        ...context.submissions.map((s) => ({ value: `PostSubmission:${s.id}`, label: s.label, group: SUPPORT_REF_TYPE_LABELS.PostSubmission })),
        ...context.payouts.map((p) => ({ value: `Payout:${p.id}`, label: p.label, group: SUPPORT_REF_TYPE_LABELS.Payout })),
        ...context.accounts.map((a) => ({ value: `SocialAccount:${a.id}`, label: a.label, group: SUPPORT_REF_TYPE_LABELS.SocialAccount })),
        ...context.bonuses.map((b) => ({ value: `ReferralBonus:${b.id}`, label: b.label, group: SUPPORT_REF_TYPE_LABELS.ReferralBonus })),
      ]
    : [];
  const groups = [...new Set(options.map((o) => o.group))];
  /* A prefilled reference the picker has not loaded yet (or does not list —
     a submission past its fifty) still has to be selectable. */
  const prefilledUnlisted = ref !== '' && !options.some((o) => o.value === ref);

  return (
    <>
      <PageHeader
        eyebrow="Creator"
        title="New ticket"
        lead="Say what it is about and what you would like to happen. A person reads it."
        actions={
          <Link className="btn btn-ghost" to="/support">
            Back to your tickets
          </Link>
        }
      />

      <Card title="What is this about?">
        <form onSubmit={submit} className="form">
          <SelectField
            label="Topic"
            name="category"
            value={category}
            onChange={(e) => setCategory(e.target.value as SupportTicketCategory)}
          >
            {SUPPORT_TICKET_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {SUPPORT_TICKET_CATEGORY_LABELS[c]}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Which one?"
            name="ref"
            value={ref}
            onChange={(e) => pickRef(e.target.value)}
            hint="Optional. Pointing the ticket at the submission, payout or account it is about saves a question."
          >
            <option value="">Nothing in particular</option>
            {prefilledUnlisted && <option value={ref}>{ref.split(':')[0]} {ref.split(':')[1]?.slice(0, 8)}…</option>}
            {groups.map((group) => (
              <optgroup key={group} label={group}>
                {options
                  .filter((o) => o.group === group)
                  .map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
              </optgroup>
            ))}
          </SelectField>

          <TextField
            label="Subject"
            type="text"
            name="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={SUPPORT_SUBJECT_MAX}
            minLength={3}
            required
            hint={`${subject.length} of ${SUPPORT_SUBJECT_MAX} characters.`}
          />

          <TextField
            label="What happened, and what would you like?"
            name="body"
            textarea
            rows={8}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={SUPPORT_BODY_MAX}
            required
            hint={`${body.length} of ${SUPPORT_BODY_MAX} characters. Links are fine; there is nothing to attach.`}
            {...(error ? { 'aria-invalid': true } : {})}
          />

          {error && <Alert tone="critical">{error}</Alert>}

          <div className="actions">
            <Button type="submit" variant="primary" disabled={busy || subject.trim().length < 3 || body.trim().length === 0}>
              {busy ? 'Opening…' : 'Open the ticket'}
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
}
